import React, { useState, useMemo } from 'react'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Divider, Skeleton } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useLocale } from 'next-intl'
import { useGetWalletFAQQuery, FAQResponse } from '@/src/api/rtkQuery/walletFAQApi'

export interface WalletFAQProps {
    initialFAQ?: FAQResponse | null
    isDesktop?: boolean
}

const WalletFAQ: React.FC<WalletFAQProps> = ({ initialFAQ, isDesktop = false }) => {
    const locale = useLocale() as 'en' | 'ar'
    const [expanded, setExpanded] = useState<string | false>(false)
    
    // Skip client fetch when we already have server-fetched data (avoids duplicate request)
    const { data: faqResponse, isLoading, isError } = useGetWalletFAQQuery(undefined, {
        skip: !!initialFAQ
    })

    // Use initial FAQ data if available, otherwise use RTK Query data
    const effectiveFAQResponse = initialFAQ || faqResponse

    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false)
    }

    // Get FAQ data based on current locale
    const faqData = useMemo(() => {
        if (!effectiveFAQResponse?.faq_data) return null
        return effectiveFAQResponse.faq_data[locale] || effectiveFAQResponse.faq_data.en
    }, [effectiveFAQResponse, locale])
    
    // Only show loading if we don't have initial data and RTK Query is loading
    const showLoading = !initialFAQ && isLoading

    // Show loading skeleton
    if (showLoading) {
        return (
            <Box
                sx={{
                    bgcolor: 'background.paper'
                }}
            >
                <Box sx={{ px: isDesktop ? 0 : 2, py: 3 }}>
                    <Skeleton variant="text" width={100} height={32} sx={{ mb: 2 }} />
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1.5, borderRadius: 2 }} />
                    ))}
                </Box>
            </Box>
        )
    }

    // Don't render if error or no data
    if (isError || !faqData || !faqData.questions || faqData.questions.length === 0) {
        return null
    }

    return (
        <Box
            sx={{
                bgcolor: 'background.paper'
            }}
        >
            <Box sx={{ px: isDesktop ? 0 : 2, py: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {faqData.title}
                </Typography>

                {faqData.questions.map((item, index) => {
                    const panelId = `faq-panel-${index}`
                    const isExpanded = expanded === panelId

                    return (
                        <Accordion
                            key={index}
                            expanded={isExpanded}
                            onChange={handleChange(panelId)}
                            elevation={0}
                            sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                mb: 1.5,
                                '&:before': {
                                    display: 'none'
                                },
                                '&.Mui-expanded': {
                                    margin: '0 0 12px 0'
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    minHeight: 56,
                                    '&.Mui-expanded': {
                                        minHeight: 56
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        margin: '12px 0',
                                        '&.Mui-expanded': {
                                            margin: '12px 0'
                                        }
                                    }
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        pr: 2
                                    }}
                                >
                                    {item.question}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{
                                    px: 2,
                                    pb: 2,
                                    pt: 0
                                }}
                            >
                                <Divider sx={{ mb: 2 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.8,
                                        whiteSpace: 'pre-line'
                                    }}
                                >
                                    {item.answer}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )
                })}
            </Box>
        </Box>
    )
}

export { WalletFAQ }
export default WalletFAQ
